'use client';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { Section, SectionHeading } from '@/components/ui/Section';
import { BUILT_FOR } from '@/data/content';
import { ROUTES } from '@/config/routes';

const BUILT_FOR_ICONS = ['gamepad', 'link', 'users', 'megaphone', 'pin'] as const;

/** Invites platform owners to bring their own product to the LaunchPad network. */
export function PartnerCta({ tone = 'default' }: { tone?: 'default' | 'raised' }) {
  return (
    <Section id="partners" tone={tone}>
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <SectionHeading
            align="left"
            eyebrow="For platform owners"
            title="Do you have a platform the LaunchPad could support?"
            description="If you run a platform or product you want in front of real foot traffic, our screens can promote it. Tell us what you have and we will work out how to put it in venue."
          />

          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {BUILT_FOR.map((audience, index) => (
              <li key={audience} className="text-content-muted flex items-center gap-3 text-sm">
                <Icon name={BUILT_FOR_ICONS[index] ?? 'spark'} className="text-brand-bright h-5 w-5" />
                {audience}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button to={ROUTES.partners}>
              Contact us now
              <Icon name="arrow" className="h-5 w-5" />
            </Button>
            <Button variant="secondary" to={ROUTES.contact}>
              Ask a question
            </Button>
          </div>
        </div>

        <Card highlighted className="flex flex-col gap-5">
          <h3 className="text-2xl text-white">What we need from you</h3>
          <ol className="flex flex-col gap-4">
            {[
              'What the platform is and who uses it.',
              'How you measure results and what success looks like.',
              'Creative we can put on screen, or let us produce it.',
              'The markets and venue types you want to reach.',
            ].map((item, index) => (
              <li key={item} className="text-content-muted flex gap-3 text-sm leading-relaxed">
                <span className="border-brand/40 bg-brand/10 text-brand-bright flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border text-xs font-bold">
                  {index + 1}
                </span>
                {item}
              </li>
            ))}
          </ol>
          <p className="border-line/70 text-content-subtle border-t pt-5 text-sm">
            We reply to every enquiry, usually within one business day.
          </p>
        </Card>
      </div>
    </Section>
  );
}
